import { useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  Box,
  Dialog,
  Divider,
  Grid,
  IconButton,
  Slide,
  Stack,
  Typography,
} from "@mui/material";
import type { TransitionProps } from "@mui/material/transitions";
import CloseIcon from "@mui/icons-material/Close";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import NotesOutlinedIcon from "@mui/icons-material/NotesOutlined";
import RotateLeftIcon from "@mui/icons-material/RotateLeft";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { setMessage, useMaterialUIController } from "context";
import { formatCurrency } from "helpers/utils";
import { formatDate, formatDateEndOfDay } from "helpers/formatDate";
import { formatCanalInscricaoLabel } from "helpers/canalInscricao";
import { IListInscricoesActive } from "interfaces/inscricoes";
import { IDetailsBoleto } from "interfaces/detailsBoleto";
import { IDetailsPix } from "interfaces/detailsPix";
import { IDetailsCartaoEfi } from "interfaces/detailsCartao";
import { cancelInscricao } from "services/inscricoes";
import ModalDetailsPay from "layouts/financeiro/componets/ModalDetailsPay";

import InscricaoStatus from "./InscricaoStatus";
import ModalBaixaManualPay from "./ModalBaixaManualPay";
import ModalCortesia from "./ModalCortesia";
import TableAtletasInscrito from "./TableAtletasInscrito";
import {
  detailHeaderSx,
  INSCRICAO_ACCENT,
  sectionCardSx,
} from "./inscricoes.theme";

const Transition = (props: TransitionProps & { children: React.ReactElement }) => (
  <Slide direction="up" {...props} />
);

function SectionCard({
  icon,
  title,
  children,
}: {
  icon: ReactNode;
  title: string;
  children: ReactNode;
}) {
  return (
    <Box sx={sectionCardSx}>
      <Stack direction="row" spacing={1} alignItems="center" mb={2}>
        <Box sx={{ color: INSCRICAO_ACCENT, display: "flex" }}>{icon}</Box>
        <MDTypography variant="button" fontWeight="bold">
          {title}
        </MDTypography>
      </Stack>
      {children}
    </Box>
  );
}

function InfoRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <Box mb={1.25}>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={500}>
        {value}
      </Typography>
    </Box>
  );
}

function ValorRow({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center" py={0.5}>
      <Typography variant="body2" color={bold ? "text.primary" : "text.secondary"} fontWeight={bold ? 700 : 400}>
        {label}
      </Typography>
      <Typography variant={bold ? "h6" : "body2"} fontWeight={bold ? 700 : 500}>
        {value}
      </Typography>
    </Stack>
  );
}

type PaymentDetails = IDetailsBoleto | IDetailsPix | IDetailsCartaoEfi | null;

type Props = {
  open: boolean;
  inscricao: IListInscricoesActive | null;
  isloading: boolean;
  detailsPayment: PaymentDetails;
  openModalBaixaManualPay: boolean;
  openModalCortesia: boolean;
  onClose: () => void;
  onDetailsPagamento: (inscricao: IListInscricoesActive) => void;
  onToggleBaixaManual: () => void;
  onCloseBaixaManual: () => void;
  onToggleCortesia: () => void;
  onCloseCortesia: () => void;
  onSubmitBaixa: (values: { observacao: string }) => void;
  onSubmitCortesia: (values: { observacao: string }) => void;
  onUpdated: () => void;
};

export default function InscricaoDetalheDialog({
  open,
  inscricao,
  isloading,
  detailsPayment,
  openModalBaixaManualPay,
  openModalCortesia,
  onClose,
  onDetailsPagamento,
  onToggleBaixaManual,
  onCloseBaixaManual,
  onToggleCortesia,
  onCloseCortesia,
  onSubmitBaixa,
  onSubmitCortesia,
  onUpdated,
}: Props) {
  const [, dispatch] = useMaterialUIController();
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [canceling, setCanceling] = useState(false);

  useEffect(() => {
    if (!open) {
      setConfirmCancel(false);
      setCanceling(false);
    }
  }, [open]);

  if (!inscricao) return null;

  const isCancelado = inscricao.statusInscricao.tipo === "Cancelado";
  const isPendente = inscricao.statusInscricao.tipo === "Pendente";
  const showBaixaActions =
    inscricao.pagamentos?.cobrancaBoleto?.status !== "identified" && isPendente;
  const atletasCount = inscricao.inscritos?.length ?? 0;

  const handleCancel = async () => {
    setCanceling(true);
    try {
      await cancelInscricao(inscricao.id);
      setMessage(dispatch, {
        open: true,
        text: "Inscrição cancelada.",
        severity: "success",
      });
      setConfirmCancel(false);
      onUpdated();
      onClose();
    } catch {
      setMessage(dispatch, {
        open: true,
        severity: "error",
        text: "Não foi possível cancelar a inscrição.",
      });
    } finally {
      setCanceling(false);
    }
  };

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={onClose}
      TransitionComponent={Transition}
      PaperProps={{ sx: { bgcolor: "grey.50" } }}
    >
      <Box sx={detailHeaderSx}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          alignItems={{ xs: "flex-start", md: "center" }}
          justifyContent="space-between"
          spacing={2}
        >
          <Stack direction="row" spacing={1.5} alignItems="flex-start" flex={1} minWidth={0}>
            <IconButton onClick={onClose} sx={{ color: "inherit", mt: -0.5 }} aria-label="Fechar">
              <CloseIcon />
            </IconButton>
            <Box minWidth={0}>
              <Typography variant="overline" sx={{ opacity: 0.75, letterSpacing: 1.2 }}>
                Detalhes da inscrição
              </Typography>
              <Typography variant="h5" fontWeight="bold" noWrap>
                {inscricao.evento.titulo}
              </Typography>
              <Stack direction="row" spacing={2} flexWrap="wrap" mt={0.5}>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Nº {inscricao.numeroInscricao}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  {formatDateEndOfDay(inscricao.evento.dataEvento, "dd/MM/yyyy")}
                </Typography>
              </Stack>
            </Box>
          </Stack>

          <Stack
            direction={{ xs: "row", sm: "column" }}
            alignItems={{ xs: "center", sm: "flex-end" }}
            spacing={1.25}
            sx={{ flexShrink: 0 }}
          >
            <Typography
              variant="h4"
              fontWeight={800}
              sx={{
                color: "#fff",
                lineHeight: 1.1,
                letterSpacing: "-0.02em",
              }}
            >
              {formatCurrency(inscricao.totalInscricao)}
            </Typography>
            <InscricaoStatus status={inscricao.statusInscricao.tipo} surface="dark" />
          </Stack>
        </Stack>
      </Box>

      {!isCancelado || showBaixaActions ? (
        <Box
          sx={{
            px: { xs: 2, md: 3 },
            py: 1.5,
            bgcolor: "background.paper",
            borderBottom: "1px solid",
            borderColor: "grey.200",
          }}
        >
          <Stack direction="row" flexWrap="wrap" gap={1} justifyContent="flex-end">
            {showBaixaActions && (
              <>
                <MDButton variant="outlined" color="dark" size="small" onClick={onToggleCortesia}>
                  Cortesia
                </MDButton>
                <MDButton variant="outlined" color="info" size="small" onClick={onToggleBaixaManual}>
                  Baixa manual
                </MDButton>
              </>
            )}
            {!isCancelado && (
              <>
                {confirmCancel ? (
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <Typography variant="body2">Confirmar cancelamento?</Typography>
                    <MDButton
                      variant="outlined"
                      color="error"
                      size="small"
                      onClick={handleCancel}
                      disabled={canceling}
                    >
                      Sim, cancelar
                    </MDButton>
                    <MDButton
                      variant="text"
                      color="dark"
                      size="small"
                      onClick={() => setConfirmCancel(false)}
                    >
                      Não
                    </MDButton>
                  </Stack>
                ) : (
                  <MDButton
                    variant="outlined"
                    color="error"
                    size="small"
                    onClick={() => setConfirmCancel(true)}
                  >
                    Cancelar inscrição
                  </MDButton>
                )}
              </>
            )}
          </Stack>
        </Box>
      ) : null}

      <Box sx={{ flex: 1, overflow: "auto", py: 3, px: { xs: 2, md: 3 } }}>
        <Box maxWidth={1200} mx="auto">
          <Grid container spacing={2.5}>
            <Grid item xs={12} md={6}>
              <SectionCard icon={<EventOutlinedIcon fontSize="small" />} title="Inscrição">
                <InfoRow
                  label="Data e hora"
                  value={formatDate(inscricao.dataInscricao, "dd/MM/yyyy HH:mm:ss")}
                />
                <InfoRow label="Evento" value={inscricao.evento.titulo} />
                <InfoRow
                  label="Data do evento"
                  value={formatDateEndOfDay(inscricao.evento.dataEvento, "dd/MM/yyyy")}
                />
                <InfoRow
                  label="Canal de inscrição"
                  value={formatCanalInscricaoLabel(
                    inscricao.canalInscricao,
                    inscricao.plataformaApp,
                  )}
                />
              </SectionCard>
            </Grid>

            <Grid item xs={12} md={6}>
              <SectionCard icon={<PersonOutlineIcon fontSize="small" />} title="Responsável">
                <InfoRow label="Nome" value={inscricao.nomeResponsavel} />
                <InfoRow label="CPF" value={inscricao.cpfResponsavel} />
              </SectionCard>
            </Grid>

            <Grid item xs={12} md={6}>
              <SectionCard icon={<PaymentsOutlinedIcon fontSize="small" />} title="Cobrança">
                {inscricao.pagamentos ? (
                  <Stack spacing={1.5}>
                    <InfoRow
                      label="Vencimento"
                      value={formatDateEndOfDay(inscricao.pagamentos.dataVencimento)}
                    />
                    <InfoRow
                      label="Data da baixa"
                      value={
                        formatDateEndOfDay(inscricao.pagamentos.dataBaixa) || (
                          <Typography component="span" color="warning.main" fontWeight={600}>
                            Aguardando
                          </Typography>
                        )
                      }
                    />
                    {inscricao.pagamentos.tipoPagamento.tipoPagamento === "Boleto" && (
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                          Boleto
                        </Typography>
                        <Typography
                          component={Link}
                          to={inscricao.pagamentos.cobrancaBoleto.pdf_change}
                          target="_blank"
                          variant="body2"
                          color="info.main"
                          fontWeight={600}
                        >
                          Abrir 2ª via do boleto
                        </Typography>
                      </Box>
                    )}
                    <Stack direction="row" alignItems="center" spacing={1.5} mt={1}>
                      <MDButton
                        variant="outlined"
                        color="dark"
                        size="small"
                        onClick={() => onDetailsPagamento(inscricao)}
                        startIcon={
                          <Box
                            component="img"
                            src={inscricao.pagamentos.tipoPagamento.icon}
                            alt=""
                            sx={{ height: 22, width: "auto" }}
                          />
                        }
                      >
                        Detalhes do pagamento
                      </MDButton>
                      {isloading && (
                        <RotateLeftIcon
                          sx={{
                            animation: "spin 1s linear infinite",
                            "@keyframes spin": {
                              "0%": { transform: "rotate(360deg)" },
                              "100%": { transform: "rotate(0deg)" },
                            },
                          }}
                        />
                      )}
                      <ModalDetailsPay paymentDetails={detailsPayment} />
                    </Stack>
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Inscrição gratuita — sem cobrança vinculada.
                  </Typography>
                )}
              </SectionCard>
            </Grid>

            <Grid item xs={12} md={6}>
              <SectionCard icon={<ReceiptLongOutlinedIcon fontSize="small" />} title="Valores">
                <ValorRow label="Subtotal" value={formatCurrency(inscricao.subTotal)} />
                <ValorRow label="Taxa" value={formatCurrency(inscricao.totalTaxa)} />
                <ValorRow
                  label="Cupom"
                  value={inscricao.cupomPromocao?.toUpperCase() || "Sem cupom"}
                />
                <ValorRow label="Desconto" value={formatCurrency(inscricao.totalDesconto)} />
                <Divider sx={{ my: 1 }} />
                <ValorRow
                  label="Total"
                  value={formatCurrency(inscricao.totalInscricao)}
                  bold
                />
              </SectionCard>
            </Grid>

            <Grid item xs={12}>
              <SectionCard icon={<NotesOutlinedIcon fontSize="small" />} title="Observação">
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-wrap" }}>
                  {inscricao.obs?.trim() || "Nenhuma observação registrada."}
                </Typography>
              </SectionCard>
            </Grid>

            <Grid item xs={12}>
              <SectionCard
                icon={<GroupsOutlinedIcon fontSize="small" />}
                title={`${atletasCount} ${atletasCount === 1 ? "atleta inscrito" : "atletas inscritos"}`}
              >
                <TableAtletasInscrito
                  handleUpdate={onUpdated}
                  data={inscricao}
                  isloading={isloading}
                />
              </SectionCard>
            </Grid>
          </Grid>
        </Box>
      </Box>

      {inscricao.pagamentos && (
        <>
          <ModalBaixaManualPay
            open={openModalBaixaManualPay}
            handleOpen={onCloseBaixaManual}
            handleSubmit={onSubmitBaixa}
            inscricao={inscricao}
          />
          <ModalCortesia
            open={openModalCortesia}
            handleOpen={onCloseCortesia}
            handleSubmit={onSubmitCortesia}
            inscricao={inscricao}
          />
        </>
      )}
    </Dialog>
  );
}
